<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="visibleItems"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      loading-text="読込中..."
      class="user-list-table"
      :class="{ 'user-list-table--select': selectMode }"
      @click:row="onRowClick"
    >
      <template #top>
        <v-toolbar
          flat
          color="transparent"
        >
          <v-btn
            small
            color="primary"
            :outlined="!selectMode"
            @click="toggleSelectMode"
          >
            {{ selectMode ? '選択中' : '選択' }}
          </v-btn>
          <v-btn
            v-if="selectMode"
            small
            text
            class="ml-2"
            @click="toggleSelectAll"
          >
            全選択
          </v-btn>
          <v-btn-toggle
            v-model="statusFilter"
            mandatory
            dense
            class="ml-4"
          >
            <v-btn
              small
              value="active"
            >
              在籍({{ activeCount }})
            </v-btn>
            <v-btn
              small
              value="retired"
            >
              退店({{ retiredCount }})
            </v-btn>
          </v-btn-toggle>
          <v-text-field
            v-model="searchText"
            placeholder="名前で検索"
            dense
            hide-details
            clearable
            :prepend-inner-icon="icons.mdiMagnify"
            class="name-search ml-4"
          />
          <v-menu
            v-model="filterMenu"
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-badge
                :content="filterCount"
                :value="filterCount"
                color="primary"
                overlap
              >
                <v-btn
                  small
                  outlined
                  class="ml-3"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ icons.mdiFilterVariant }}
                  </v-icon>
                  フィルタ
                </v-btn>
              </v-badge>
            </template>
            <v-card class="pa-4 filter-panel">
              <div class="text-caption mb-1">
                ラベル
              </div>
              <v-chip-group
                v-model="filterFlagIds"
                multiple
                column
              >
                <v-chip
                  v-for="flag in userSystemFlags"
                  :key="`filter-flag-${flag.id}`"
                  :value="Number(flag.id)"
                  small
                  filter
                  outlined
                >
                  {{ flag.attributes.name }}
                </v-chip>
              </v-chip-group>
              <template v-if="!aggregationRoleBasedEnabled">
                <v-divider class="my-2" />
                <v-checkbox
                  v-model="filterUnassigned"
                  label="集計未割当のみ"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                />
              </template>
              <div class="d-flex mt-4">
                <v-btn
                  small
                  text
                  color="primary"
                  @click="clearFilters"
                >
                  クリア
                </v-btn>
                <v-spacer />
                <v-btn
                  small
                  text
                  @click="filterMenu = false"
                >
                  閉じる
                </v-btn>
              </div>
            </v-card>
          </v-menu>
          <v-spacer />
          <v-btn
            small
            color="primary"
            @click="$emit('add-property')"
          >
            <v-icon left>
              {{ icons.mdiPlus }}
            </v-icon>
            プロパティを追加
          </v-btn>
        </v-toolbar>

        <!-- 適用中フィルタのチップ -->
        <div
          v-if="filterCount"
          class="d-flex flex-wrap px-4 pb-2"
        >
          <v-chip
            v-for="flagId in filterFlagIds"
            :key="`active-filter-${flagId}`"
            small
            close
            class="mr-1 mb-1"
            @click:close="removeFlagFilter(flagId)"
          >
            ラベル: {{ flagNameOf(flagId) }}
          </v-chip>
          <v-chip
            v-if="filterUnassigned"
            small
            close
            class="mr-1 mb-1"
            @click:close="filterUnassigned = false"
          >
            集計未割当のみ
          </v-chip>
        </div>
      </template>

      <template #[`item.select`]="{ item }">
        <v-simple-checkbox
          :value="selectedIds.includes(item.id)"
          class="select-checkbox"
          color="primary"
        />
      </template>

      <template
        v-for="header in customHeaders"
        #[`header.${header.value}`]
      >
        <span :key="`header-text-${header.value}`">
          {{ header.text }}
          <v-icon
            x-small
            class="cf-editable-icon"
          >
            {{ icons.mdiPencilOutline }}
          </v-icon>
          <v-menu
            offset-y
            left
            :close-on-content-click="true"
            content-class="custom-header-menu"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                class="ml-1 custom-header-menu-btn"
                style="margin-top: -3px;"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>
                  {{ icons.mdiDotsVertical }}
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>操作</v-subheader>
              <v-list-item @click.stop="$emit('edit-property', header)">
                <v-list-item-icon class="mr-2">
                  <v-icon small>
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>編集</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click.stop="$emit('delete-property', header)">
                <v-list-item-icon class="mr-2">
                  <v-icon small>
                    {{ icons.mdiTrashCanOutline }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>削除</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
      </template>

      <template #[`item.attributes.profileImage`]="{ item }">
        <v-avatar
          size="40"
          color="secondary"
        >
          <v-img
            v-if="item.attributes.profileImage"
            :src="item.attributes.profileImage"
          />
          <span
            v-else
            class="text-caption font-weight-medium"
          >
            {{ item.attributes.name.substring(0, 3) }}
          </span>
        </v-avatar>
      </template>

      <template #[`item.attributes.name`]="{ item }">
        {{ item.attributes.name }}
      </template>

      <!-- ラベルは先頭2個+「+N」で行の高さを1行に揃える(全ラベルは編集ダイアログで確認) -->
      <template #[`item.userSystemFlaggingFlags`]="{ item }">
        <div class="label-cell">
          <v-chip
            v-for="flag in item.userSystemFlaggingFlags.slice(0, 2)"
            :key="flag.id"
            small
          >
            {{ flag.name }}
          </v-chip>
          <v-chip
            v-if="item.userSystemFlaggingFlags.length > 2"
            small
            outlined
          >
            +{{ item.userSystemFlaggingFlags.length - 2 }}
          </v-chip>
        </div>
      </template>

      <template
        v-for="header in customHeaders"
        #[`item.${header.value}`]="{ item }"
      >
        <div
          :key="`cf-${header.value}-${refreshTick}`"
          class="cf-cell"
          :class="{ 'cf-cell--saved': savedCellKey === cellKeyOf(item, header) }"
          @click.stop="onCellClick(item)"
        >
          <v-checkbox
            v-if="header.type === 'boolean'"
            :input-value="item[header.value] === 'true'"
            :disabled="selectMode"
            dense
            hide-details
            class="ma-0 pa-0"
            @change="onFieldChange(item, header, $event ? 'true' : 'false')"
          />
          <input
            v-else
            :type="header.type === 'number' ? 'number' : 'text'"
            class="cf-input"
            :class="{ 'cf-input--number': header.type === 'number' }"
            :value="item[header.value]"
            :disabled="selectMode"
            placeholder="—"
            @change="onFieldChange(item, header, $event.target.value)"
            @keydown.enter="$event.target.blur()"
          >
        </div>
      </template>

      <!-- 集計ラベル: タップで専用の集計割当ダイアログ(編集ダイアログの集計タブと同じ動作)を開く。
           行クリック(編集ダイアログ)には伝播させない -->
      <template #[`item.aggregationCount`]="{ item }">
        <span
          class="d-inline-block"
          @click.stop="onAggregationCellClick(item)"
        >
          <v-chip
            x-small
            outlined
            :color="item.aggregationCount === 0 ? 'warning' : undefined"
          >
            {{ item.aggregationCount === 0 ? '未割当' : `${item.aggregationCount}件` }}
          </v-chip>
        </span>
      </template>

      <template #[`item.actions`]>
        <v-icon small>
          {{ icons.mdiChevronRight }}
        </v-icon>
      </template>
    </v-data-table>

    <!-- 集計割当ダイアログ(集計ラベルから。編集ダイアログの集計タブと同じ動作) -->
    <v-dialog
      v-if="aggDialogUser"
      v-model="aggDialog"
      width="500"
    >
      <v-card>
        <v-card-title>集計割当 — {{ aggDialogUser.attributes.name }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="aggDialogTemplate"
            :items="aggregationCatalogs"
            item-text="attributes.name"
            item-value="id"
            return-object
            label="集計テンプレート"
            no-data-text="利用可能なデータがありません"
            clearable
            hint="テンプレートを選ぶと既存の割当が削除対象(赤)になり、テンプレートの内容に置き換わります"
            persistent-hint
            class="mb-4"
          />
          <template v-if="aggDialogTemplate">
            <div class="text-caption mb-1">
              新しく割り当てられる集計表
            </div>
            <div
              v-for="group in aggDialogTemplate.attributes.aggregations"
              :key="`agg-tpl-group-${group.type}`"
            >
              <v-chip
                v-for="(agg, aggIdx) in group.aggregations"
                :key="`agg-tpl-${group.type}-${aggIdx}`"
                small
                color="primary"
                outlined
                class="mr-1 mb-1"
              >
                {{ agg.name }}
              </v-chip>
            </div>
            <v-divider class="my-3" />
          </template>
          <div class="text-caption mb-1">
            現在の割当(赤=削除対象。チップをタップで切替)
          </div>
          <aggregation-list
            v-if="aggDialogAggregations.length"
            :aggregations="aggDialogAggregations"
            @update:aggregations="toggleAggDialogDestroy"
          />
          <div
            v-else
            class="text-body-2"
          >
            割当はありません
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="isAggDialogSaving"
            @click="aggDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :disabled="isAggDialogSaving"
            :loading="isAggDialogSaving"
            @click="saveAggDialog"
          >
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- テンプレート適用ダイアログ -->
    <v-dialog
      v-model="templateDialog"
      width="440"
    >
      <v-card>
        <v-card-title>テンプレート適用</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            選択中の{{ selectedIds.length }}人の集計割当を、テンプレートの内容に置き換えます(各自の既存の割当は削除されます)
          </div>
          <v-select
            v-model="selectedTemplate"
            :items="aggregationCatalogs"
            item-text="attributes.name"
            item-value="id"
            return-object
            label="集計テンプレート"
            no-data-text="利用可能なデータがありません"
          />
          <template v-if="selectedTemplate">
            <v-divider class="mb-3" />
            <div class="text-caption mb-1">
              適用される集計表
            </div>
            <div
              v-for="group in selectedTemplate.attributes.aggregations"
              :key="`tpl-group-${group.type}`"
            >
              <v-chip
                v-for="(agg, aggIdx) in group.aggregations"
                :key="`tpl-agg-${group.type}-${aggIdx}`"
                small
                class="mr-1 mb-1"
              >
                {{ agg.name }}
              </v-chip>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="isBulkUpdating"
            @click="templateDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedTemplate || isBulkUpdating"
            :loading="isBulkUpdating"
            @click="execApplyTemplate"
          >
            適用
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-sheet
      v-if="selectMode"
      class="bulk-bar d-flex align-center px-4 py-2"
      elevation="4"
    >
      <span class="text-subtitle-2">{{ selectedIds.length }}人選択中</span>
      <v-btn
        v-if="statusFilter === 'active'"
        small
        color="warning"
        class="ml-3"
        :disabled="!retireTargetIds.length || isBulkUpdating"
        :loading="isBulkUpdating"
        @click="execBulk(true)"
      >
        退店にする{{ retireTargetIds.length ? `(${retireTargetIds.length})` : '' }}
      </v-btn>
      <v-btn
        v-if="statusFilter === 'active' && !aggregationRoleBasedEnabled"
        small
        outlined
        color="primary"
        class="ml-2"
        :disabled="!selectedIds.length || isBulkUpdating"
        @click="openTemplateDialog"
      >
        テンプレート適用
      </v-btn>
      <v-btn
        v-else
        small
        outlined
        class="ml-3"
        :disabled="!unretireTargetIds.length || isBulkUpdating"
        :loading="isBulkUpdating"
        @click="execBulk(false)"
      >
        退店を解除{{ unretireTargetIds.length ? `(${unretireTargetIds.length})` : '' }}
      </v-btn>
      <v-spacer />
      <v-btn
        small
        text
        :disabled="isBulkUpdating"
        @click="cancelSelect"
      >
        キャンセル
      </v-btn>
    </v-sheet>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiDotsVertical,
  mdiTrashCanOutline,
  mdiChevronRight,
  mdiMagnify,
  mdiFilterVariant,
} from '@mdi/js'
import AggregationRoleApi from '@/api/v2/employee/user-management/AggregationRole'
import AggregationList from './AggregationList.vue'

export default {
  name: 'UserList',
  components: {
    AggregationList,
  },
  props: {
    users: {
      type: Array,
      default: () => [],
      required: true,
    },
    customHeaders: {
      type: Array,
      default: () => [],
    },
    userSystemFlags: {
      type: Array,
      default: () => [],
      required: true,
    },
    aggregationCatalogs: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const selectMode = ref(false)
    const selectedIds = ref([])
    const statusFilter = ref('active')
    const searchText = ref('')
    const filterMenu = ref(false)
    const filterFlagIds = ref([])
    const filterUnassigned = ref(false)
    const templateDialog = ref(false)
    const selectedTemplate = ref(null)
    const aggDialog = ref(false)
    const aggDialogUser = ref(null)
    const aggDialogTemplate = ref(null)
    const aggDialogAggregations = ref([])
    const isAggDialogSaving = ref(false)

    // ロールベース集計が有効な店舗では割当が自動決定のため、集計列・未割当フィルタ・テンプレート適用を出さない
    const aggregationRoleBasedEnabled = ref(false)
    const fetchAggregationRoleBased = async () => {
      try {
        const res = await AggregationRoleApi.getAggregationRoles()
        aggregationRoleBasedEnabled.value = !!res.data?.data?.aggregationRoleBasedEnabled
      } catch {
        // ignore
      }
    }
    fetchAggregationRoleBased()
    const isBulkUpdating = ref(false)
    const savedCellKey = ref(null)
    const refreshTick = ref(0)
    let flashTimer = null

    const findCustomFieldValue = (item, fieldId) => {
      const fieldValue = item.attributes.userCustomFieldValues?.data?.find(value => Number(value.attributes.storeCustomFieldId) === Number(fieldId))

      return fieldValue?.attributes?.value || ''
    }

    const findSystemFlagName = flagId => {
      const flag = props.userSystemFlags.find(f => Number(f.id) === Number(flagId))

      return flag?.attributes?.name || ''
    }

    const formatDate = value => {
      const date = new Date(value)

      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    }

    // 集計割当の件数(serializerのaggregationsはグループ配列)
    const countAggregations = user => (user.attributes.aggregations || [])
      .reduce((sum, group) => sum + (group.aggregations?.length || 0), 0)

    const transformedItems = computed(() => {
      return props.users.map(user => {
        const transformed = {
          ...user,
          isRetired: !!user.attributes.retiredAt,
          retiredAtText: user.attributes.retiredAt ? formatDate(user.attributes.retiredAt) : '',
          flagIds: (user.attributes.userSystemFlaggings?.data || []).map(flagging => Number(flagging.attributes.userSystemFlagId)),
          aggregationCount: countAggregations(user),
          userSystemFlaggingFlags: (user.attributes.userSystemFlaggings?.data || []).map(flagging => ({
            id: flagging.id,
            name: findSystemFlagName(flagging.attributes.userSystemFlagId),
          })),
        }

        // カスタムヘッダーの値を追加
        props.customHeaders.forEach(header => {
          transformed[header.value] = findCustomFieldValue(user, header.value)
        })

        return transformed
      })
    })

    const activeCount = computed(() => transformedItems.value.filter(item => !item.isRetired).length)
    const retiredCount = computed(() => transformedItems.value.filter(item => item.isRetired).length)

    const filterCount = computed(() => filterFlagIds.value.length + (filterUnassigned.value ? 1 : 0))

    // 在籍/退店は混在させず切替表示。退店タブは退店日の新しい順
    const visibleItems = computed(() => {
      const term = (searchText.value || '').trim()
      const filtered = transformedItems.value
        .filter(item => (statusFilter.value === 'retired' ? item.isRetired : !item.isRetired))
        .filter(item => !term || (item.attributes.name || '').includes(term))
        .filter(item => !filterFlagIds.value.length || item.flagIds.some(id => filterFlagIds.value.includes(id)))
        .filter(item => !filterUnassigned.value || item.aggregationCount === 0)

      if (statusFilter.value === 'retired') {
        return [...filtered].sort((a, b) => (b.attributes.retiredAt || '').localeCompare(a.attributes.retiredAt || ''))
      }

      return filtered
    })

    const selectedItems = computed(() => transformedItems.value.filter(item => selectedIds.value.includes(item.id)))
    const retireTargetIds = computed(() => selectedItems.value.filter(item => !item.isRetired).map(item => item.id))
    const unretireTargetIds = computed(() => selectedItems.value.filter(item => item.isRetired).map(item => item.id))

    const headers = computed(() => [
      ...(selectMode.value ? [{
        text: '',
        value: 'select',
        sortable: false,
        width: '48',
        align: 'center',
        class: 'fixed-cell-select',
        cellClass: 'fixed-cell-select',
      }] : []),
      {
        text: 'アイコン',
        value: 'attributes.profileImage',
        sortable: false,
        width: '80',
        align: 'center',
        class: 'fixed-cell-icon',
        cellClass: 'fixed-cell-icon',
      },
      {
        text: '名前',
        value: 'attributes.name',
        sortable: true,
        width: '130',
        class: 'fixed-cell-name',
        cellClass: 'fixed-cell-name',
      },
      ...(!aggregationRoleBasedEnabled.value ? [{
        text: '集計',
        value: 'aggregationCount',
        sortable: true,
        width: '90',
      }] : []),
      {
        text: 'ラベル',
        value: 'userSystemFlaggingFlags',
        sortable: false,
        width: '140',
      },
      ...(statusFilter.value === 'retired' ? [{
        text: '退店日',
        value: 'retiredAtText',
        sortable: true,
        width: '110',
      }] : []),
      // 数値型はセル(右寄せ入力)に合わせてヘッダも右寄せにする。
      // 既定幅を与えて左に詰め、余り幅は幅指定のない右端の列に吸収させる
      ...(props.customHeaders || []).map(header => ({
        ...header,
        width: header.width || '150',
        align: header.type === 'number' ? 'end' : header.align,
      })),
      {
        text: '',
        value: 'actions',
        sortable: false,
        align: 'end',
      },
    ])

    // タブ切替時は選択をリセット(非表示行の選択が残らないように)
    watch(statusFilter, () => {
      selectedIds.value = []
    })

    const toggleSelect = item => {
      const idx = selectedIds.value.indexOf(item.id)
      if (idx >= 0) {
        selectedIds.value.splice(idx, 1)
      } else {
        selectedIds.value.push(item.id)
      }
    }

    // 選択モード中は行のどこをタップしてもチェックが切り替わる
    const onRowClick = item => {
      if (selectMode.value) {
        toggleSelect(item)
      } else {
        emit('edit', item)
      }
    }

    // 編集セルは行クリック(編集ダイアログ)に伝播させない。選択モード中はチェック切替として扱う
    const onCellClick = item => {
      if (selectMode.value) toggleSelect(item)
    }

    const toggleSelectMode = () => {
      selectMode.value = !selectMode.value
      selectedIds.value = []
    }

    const cancelSelect = () => {
      selectMode.value = false
      selectedIds.value = []
    }

    const toggleSelectAll = () => {
      const ids = visibleItems.value.map(item => item.id)
      const allSelected = ids.every(id => selectedIds.value.includes(id))
      selectedIds.value = allSelected ? [] : ids
    }

    const execBulk = retire => {
      const ids = retire ? retireTargetIds.value : unretireTargetIds.value
      if (!ids.length) return

      isBulkUpdating.value = true
      emit('bulk-retire', {
        ids,
        retire,
        onDone: () => {
          isBulkUpdating.value = false
          selectMode.value = false
          selectedIds.value = []
        },
      })
    }

    const clearFilters = () => {
      filterFlagIds.value = []
      filterUnassigned.value = false
    }

    const removeFlagFilter = flagId => {
      filterFlagIds.value = filterFlagIds.value.filter(id => id !== flagId)
    }

    const flagNameOf = flagId => {
      const flag = props.userSystemFlags.find(f => Number(f.id) === Number(flagId))

      return flag?.attributes?.name || ''
    }

    const openTemplateDialog = () => {
      selectedTemplate.value = null
      templateDialog.value = true
    }

    // 集計ラベルのタップ: 選択モード中はチェック切替、通常時は集計割当ダイアログを開く
    const onAggregationCellClick = item => {
      if (selectMode.value) {
        toggleSelect(item)

        return
      }
      aggDialogUser.value = item
      aggDialogTemplate.value = null
      aggDialogAggregations.value = JSON.parse(JSON.stringify(item.attributes.aggregations || []))
      aggDialog.value = true
    }

    // 編集ダイアログのhandleAggregationTemplateInputと同じ:
    // テンプレートを選択したら既存の割当をすべて削除対象にする(=テンプレートで置き換え)。
    // クリアしたら元の割当状態に戻す
    watch(aggDialogTemplate, template => {
      if (!aggDialogUser.value) return

      if (template) {
        aggDialogAggregations.value = aggDialogAggregations.value.map(group => ({
          ...group,
          aggregations: group.aggregations.map(agg => ({ ...agg, _destroy: true })),
        }))
      } else {
        aggDialogAggregations.value = JSON.parse(JSON.stringify(aggDialogUser.value.attributes.aggregations || []))
      }
    })

    // 編集ダイアログのupdateAggregationsと同じ: チップのタップで_destroyフラグを切り替える
    const toggleAggDialogDestroy = aggregation => {
      aggDialogAggregations.value = aggDialogAggregations.value.map(group => {
        const target = group.aggregations.find(agg => agg.type === aggregation.type && agg.ids === aggregation.ids)
        if (target) {
          target._destroy = !target._destroy
        }

        return group
      })
    }

    // 編集ダイアログの保存と同じペイロード(テンプレートID+削除フラグ付き割当)で更新する
    const saveAggDialog = () => {
      const deletions = aggDialogAggregations.value
        .map(group => group.aggregations)
        .flat()
        .filter(agg => agg._destroy)
        .map(agg => ({ ids: agg.ids, type: agg.type }))

      isAggDialogSaving.value = true
      emit('save-aggregations', {
        id: aggDialogUser.value.id,
        userAggregationTemplateId: aggDialogTemplate.value ? aggDialogTemplate.value.id : null,
        aggregations: deletions,
        callback: () => {},
        onDone: success => {
          isAggDialogSaving.value = false
          if (success) {
            aggDialog.value = false
          }
        },
      })
    }

    const execApplyTemplate = () => {
      if (!selectedTemplate.value || !selectedIds.value.length) return

      // 編集ダイアログと同じ置き換え動作: 各自の既存割当をすべて削除対象として送る
      const targets = selectedItems.value.map(item => ({
        id: item.id,
        aggregations: (item.attributes.aggregations || [])
          .map(group => group.aggregations)
          .flat()
          .map(agg => ({ ids: agg.ids, type: agg.type })),
      }))

      isBulkUpdating.value = true
      emit('bulk-apply-template', {
        targets,
        templateId: selectedTemplate.value.id,
        templateName: selectedTemplate.value.attributes.name,
        onDone: () => {
          isBulkUpdating.value = false
          templateDialog.value = false
          selectMode.value = false
          selectedIds.value = []
          selectedTemplate.value = null
        },
      })
    }

    const cellKeyOf = (item, header) => `${item.id}-${header.value}`

    const flashCell = key => {
      savedCellKey.value = null
      requestAnimationFrame(() => {
        savedCellKey.value = key
      })
      clearTimeout(flashTimer)
      flashTimer = setTimeout(() => {
        savedCellKey.value = null
      }, 1300)
    }

    const onFieldChange = (item, header, newValue) => {
      const current = item[header.value] || ''
      if (String(newValue) === String(current)) return

      const existing = item.attributes.userCustomFieldValues?.data?.find(value => Number(value.attributes.storeCustomFieldId) === Number(header.value))
      const attrs = { storeCustomFieldId: Number(header.value), value: newValue }
      if (existing) attrs.id = existing.id

      emit('save-custom-field', {
        id: item.id,
        userCustomFieldValuesAttributes: [attrs],
        fieldName: header.text,
        onSuccess: () => flashCell(cellKeyOf(item, header)),
        // 保存失敗時は入力を元の値に戻す(セルを再生成)
        onError: () => {
          refreshTick.value += 1
        },
      })
    }

    return {
      selectMode,
      selectedIds,
      statusFilter,
      searchText,
      filterMenu,
      filterFlagIds,
      filterUnassigned,
      filterCount,
      templateDialog,
      selectedTemplate,
      aggDialog,
      aggDialogUser,
      aggDialogTemplate,
      aggDialogAggregations,
      isAggDialogSaving,
      aggregationRoleBasedEnabled,
      clearFilters,
      removeFlagFilter,
      flagNameOf,
      openTemplateDialog,
      onAggregationCellClick,
      toggleAggDialogDestroy,
      saveAggDialog,
      execApplyTemplate,
      activeCount,
      retiredCount,
      isBulkUpdating,
      savedCellKey,
      refreshTick,
      headers,
      visibleItems,
      transformedItems,
      retireTargetIds,
      unretireTargetIds,
      onRowClick,
      onCellClick,
      toggleSelectMode,
      toggleSelectAll,
      cancelSelect,
      execBulk,
      cellKeyOf,
      onFieldChange,
      findCustomFieldValue,
      findSystemFlagName,
      icons: {
        mdiPlus,
        mdiPencilOutline,
        mdiDotsVertical,
        mdiTrashCanOutline,
        mdiChevronRight,
        mdiMagnify,
        mdiFilterVariant,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

@include theme--child(user-list-table) using ($material) {
  .fixed-cell-icon {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 3;
    background: map-deep-get($material, 'app-bar');
    width: 80px !important;
  }

  .fixed-cell-name {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 80px;
    z-index: 3;
    background: map-deep-get($material, 'app-bar');
  }

  th.fixed-cell-icon,
  th.fixed-cell-name {
    z-index: 4;
  }

  &.user-list-table--select {
    .fixed-cell-select {
      position: sticky !important;
      position: -webkit-sticky !important;
      left: 0;
      z-index: 3;
      background: map-deep-get($material, 'app-bar');
      width: 48px !important;
    }

    th.fixed-cell-select {
      z-index: 4;
    }

    .fixed-cell-icon {
      left: 48px;
    }

    .fixed-cell-name {
      left: 128px;
    }
  }

  .cf-input {
    border-bottom: 1px dashed map-deep-get($material, 'dividers');
  }

  .v-progress-linear {
    z-index: 5;
  }
}
</style>

<style scoped>
.custom-header-menu-btn {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.custom-header-menu-btn:hover {
  opacity: 1;
}

/* ソートアイコンとの位置調整 */
::v-deep .v-data-table__wrapper th {
  white-space: nowrap;
}

::v-deep .v-data-table-header__icon {
  margin-left: 4px;
}

/* 行をクリック可能にする */
::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

::v-deep .v-data-table__wrapper tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

::v-deep .v-data-table__wrapper tbody tr:hover .fixed-cell-icon,
::v-deep .v-data-table__wrapper tbody tr:hover .fixed-cell-name,
::v-deep .v-data-table__wrapper tbody tr:hover .fixed-cell-select {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* ラベルセルは折り返さず1行で表示 */
.label-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

/* 名前検索ボックス */
.name-search {
  max-width: 220px;
}

/* フィルタパネル */
.filter-panel {
  width: 300px;
}

/* 選択チェックは行タップで切り替える(直接操作させない) */
.select-checkbox {
  pointer-events: none;
}

/* カスタムフィールド列の編集可能アイコン */
.cf-editable-icon {
  opacity: 0.5;
  vertical-align: -1px;
}

/* インライン編集セル(枠なし+下線、日報βの集計表に合わせる) */
.cf-cell {
  min-width: 72px;
}

.cf-input {
  width: 100%;
  min-width: 64px;
  /* 列が余り幅を吸収しても入力欄(下線)は間延びさせない */
  max-width: 180px;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 2px 0;
}

.cf-input:focus {
  border-bottom: 2px solid #9155fd !important;
  margin-bottom: -1px;
}

.cf-input:disabled {
  border-bottom-color: transparent !important;
  opacity: 0.6;
}

.cf-input--number {
  text-align: right;
  -moz-appearance: textfield;
  font-variant-numeric: tabular-nums;
}

.cf-input--number::-webkit-outer-spin-button,
.cf-input--number::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.cf-cell--saved {
  animation: cf-saved-flash 1.2s ease-out;
}

@keyframes cf-saved-flash {
  0% {
    background: rgba(86, 202, 0, 0.18);
  }

  100% {
    background: transparent;
  }
}

/* 一括操作バー(画面下に固定) */
.bulk-bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
}
</style>
